import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  DocumentsList,
  FormFieldSet,
  Modal,
  PageHeader,
  PageTitle,
  useFormHandler,
  TableAdv,
  SmartTooltip,
  showToast,
  Backdrop,
  LogoLoader,
} from "../../../../components";
import { mapTableRows } from "../../../../utils";
import { v4 as uuidv4 } from "uuid";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  addNewJournalEntry,
  fetchOneJournalEntry,
  updateJournalEntry,
} from "../../../../redux/slices/journalEntries/journalEntriesApi";
import { useLoginUser } from "../../../../hooks/useLoginUser";
import { useDispatch } from "react-redux";
import { uploadStart } from "../../../../redux/slices/universal/fileUploadSlice";

const getInitialFormData = (mode, editData) => {
  if (mode === "edit" && editData) {
    return {
      entries: [
        {
          id: editData?.id || uuidv4(),
          date: editData?.date || "",
          journalNo:
            editData?.uiid || editData?.journalId || editData?.journalNo || "",
          description:
            editData?.description || editData?.transactionDetails || "",
          debit: editData?.debit || "",
          credit: editData?.credit || "",
          attachments: editData?.attachments || [],
          employee: editData?.employee || editData?.employeeId || "",
          exchangeRate: editData?.exchangeRate || "",
          dollarAmount: editData?.dollarAmount || "",
        },
      ],
    };
  }

  // CREATE MODE
  return {
    entries: [
      {
        id: uuidv4(),
        date: "",
        journalNo: "",
        description: "",
        debit: "",
        credit: "",
        attachments: [],
        employee: "",
        exchangeRate: "",
        dollarAmount: "",
      },
      {
        id: uuidv4(),
        date: "",
        journalNo: "",
        description: "",
        debit: "",
        credit: "",
        attachments: [],
        employee: "",
        exchangeRate: "",
        dollarAmount: "",
      },
    ],
  };
};

const JournalEntry = () => {
  const navigate = useNavigate();
  const { journalId } = useParams();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const mode = journalId ? "edit" : "create";
  const editData = state?.data ?? null;
  const totalCount = state?.total ?? null;
  const onBack = () => navigate("/off-trx");

  const [initialData, setInitialData] = useState(() =>
    getInitialFormData(mode, editData),
  );
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attachmentModal, setAttachmentModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;

  const { formData, handleChange, setFormData } = useFormHandler(initialData, {
    // journalNo: { type: "text", required: true },
    // date: { type: "date", required: true },
    // entries: [
    //   {
    //     date: { type: "date", required: true },
    //     description: { type: "text", required: true },
    //     debit: { type: "text" },
    //     credit: { type: "text" },
    //   },
    // ],
  });

  const addRow = () => {
    const newRow = {
      id: uuidv4(),
      date: "",
      journalNo: "",
      description: "",
      debit: "",
      credit: "",
      attachments: [],
      employee: "",
      exchangeRate: "",
      dollarAmount: "",
    };
    setFormData({ ...formData, entries: [...formData?.entries, newRow] });
  };

  const removeRow = (id) => {
    setFormData({
      ...formData,
      entries: formData?.entries?.filter((row) => row?.id !== id),
    });
  };

  const duplicateRow = (index) => {
    const currentRow = formData?.entries[index];
    const duplicatedRow = { ...currentRow, id: uuidv4() };
    const updatedEntries = [...formData?.entries];
    updatedEntries.splice(index + 1, 0, duplicatedRow);
    setFormData({ ...formData, entries: updatedEntries });
  };

  const handleReset = () => {
    setFormData(initialData);
  };

  useEffect(() => {
    const loadEditEntry = async () => {
      if (mode !== "edit" || editData || !journalId) return;

      setLoading(true);
      try {
        const response = await fetchOneJournalEntry({
          JournalEntryUiid: journalId,
          retrieve: "",
          ref: "",
          incl: "",
        });

        if (!response?.success) {
          showToast(
            response?.error || response?.msg || "Unable to load journal entry",
            "error",
            3000,
          );
          return;
        }

        const journalResponse = response?.data;
        const entry = Array.isArray(journalResponse)
          ? journalResponse[0]
          : journalResponse;

        if (entry) {
          const loadedInitialData = getInitialFormData("edit", {
            id: entry._id || entry.uiid || journalId,
            date: entry.date || "",
            journalId: entry.uiid || entry.journalId || entry.journalNo || "",
            description: entry.description || entry.transactionDetails || "",
            debit: entry.debit || "",
            credit: entry.credit || "",
            attachments: entry.attachments || [],
            employee: entry.employee || entry.employeeId || "",
            exchangeRate: entry.exchangeRate || "",
            dollarAmount: entry.dollarAmount || "",
          });
          setInitialData(loadedInitialData);
          setFormData(loadedInitialData);
        }
      } catch (error) {
        showToast(
          error?.error || error?.message || "Unable to load journal entry",
          "error",
          3000,
        );
      } finally {
        setLoading(false);
      }
    };

    loadEditEntry();
  }, [editData, journalId, mode, setFormData]);

  const handleSave = async () => {
    setSubmitting(true);
    setLoading(true);
  
    try {
      // 1. Upload attachments (keep parallel — this is fine)
      const uploadResults = await Promise.all(
        formData.entries.map((entry, index) => {
          if (entry.attachments?.length) {
            const newFiles = entry.attachments.filter((a) => a instanceof File);
  
            if (newFiles.length) {
              return new Promise((resolve) => {
                dispatch(
                  uploadStart({
                    uploadType: "multiple",
                    field: "doc",
                    uiid: `temp_${index}`,
                    entity: "journalentrys",
                    category: "journalentrys",
                    coll: "JournalEntry",
                    doc: newFiles,
                    addedBy: loginUserUiid,
                    onSuccess: (fileData) => {
                      resolve(fileData.map((file) => file.key));
                    },
                    onError: () => resolve([]),
                  })
                );
              });
            }
          }
          return Promise.resolve([]);
        })
      );
  
      // 2. Build payload
      const payload = formData.entries.map((entry, index) => {
        const existingAttachments = entry.attachments.filter(
          (a) => typeof a === "string"
        );
  
        const newKeys = uploadResults[index] || [];
  
        return {
          date: entry.date,
          ...(mode === "edit" && { status: 0 }),
          description: entry.description,
          debit: entry.debit,
          credit: entry.credit,
          attachments: [...existingAttachments, ...newKeys],
          employeeId: entry?.employee?.value || entry.employee || "",
          exchangeRate: entry.exchangeRate,
          dollarAmount: entry.dollarAmount,
          addedBy: loginUserUiid,
          updatedBy: loginUserUiid,
        };
      });
  
      let responses = [];
  
      // 3. CREATE → sequential execution (your requirement)
      if (mode === "create") {
        for (let i = 0; i < payload.length; i++) {
          const entry = payload[i];
  
          try {
            const response = await addNewJournalEntry(entry);
            responses.push(response);
  
            // Optional: progress tracking
            console.log(`Created ${i + 1}/${payload.length}`);
  
            if (!response?.success) {
              throw new Error(`Failed at entry ${i + 1}`);
            }
          } catch (error) {
            responses.push({ success: false, error });
  
            // HARD STOP (intentional)
            throw new Error(
              `Stopped at ${i + 1}/${payload.length} - ${error.message}`
            );
          }
        }
      }
  
      // 4. UPDATE (single call)
      else {
        const response = await updateJournalEntry({
          coll: "JournalEntry",
          uiid: journalId,
          payload: [{ type: "SET", value: payload?.[0] }],
        });
  
        responses = [response];
      }
  
      // 5. Final validation
      const failedCount = responses.filter((r) => !r?.success).length;
  
      if (failedCount === 0) {
        showToast(
          mode === "create"
            ? `${payload.length} journal entries created successfully`
            : "Journal entry updated successfully",
          "success",
          3000
        );
  
        handleReset();
        onBack?.();
      } else {
        throw new Error(
          mode === "create"
            ? `${failedCount} out of ${payload.length} failed`
            : "Update failed"
        );
      }
    } catch (error) {
      showToast(
        error?.error || error?.message || "Unable to save journal entry",
        "error",
        3000
      );
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };
  const employeeOptions = [
    { label: "Raghu", value: "raghu" },
    { label: "Aishwarya Vedula", value: "Aishwarya Vedula" },
    { label: "Abdul Waseem Syed", value: "Abdul Waseem Syed" },
    { label: "Amol Dasani", value: "Amol Dasani" },
  ];

  const existingAttachments =
    formData?.entries?.[selectedIndex]?.attachments?.filter(
      (a) => typeof a === "string",
    ) || [];
  const newAttachments =
    formData?.entries?.[selectedIndex]?.attachments?.filter(
      (a) => a instanceof File,
    ) || [];

  const existingAttachmentDocs = existingAttachments.map((attachment) => ({
    name: attachment?.split("/").pop() || attachment,
    filePath: attachment,
  }));

  const handleRemoveAttachment = (index, isNew) => {
    const currentAttachments = formData.entries[selectedIndex].attachments;
    if (isNew) {
      // Remove from new attachments
      const updated = currentAttachments.filter(
        (a) => !(a instanceof File) || currentAttachments.indexOf(a) !== index,
      );
      handleChange(`entries[${selectedIndex}].attachments`, updated);
    } else {
      // For existing, remove the string
      const updated = currentAttachments.filter(
        (a) => typeof a !== "string" || currentAttachments.indexOf(a) !== index,
      );
      handleChange(`entries[${selectedIndex}].attachments`, updated);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "journalNo",
        label: "Journal No",
        render: (row) => (
          <FormFieldSet
            noTitle
            type="text"
            placeholder="Journal No"
            value={
              mode === "create"
                ? totalCount + row?._index + 1
                : row?.journalNo === "-"
                  ? ""
                  : row?.journalNo
            }
            onChange={(value) =>
              handleChange(`entries[${row?._index}].journalNo`, value)
            }
            disabled={true}
          />
        ),
      },
      {
        key: "date",
        label: "Date",
        render: (row) => (
          <FormFieldSet
            noTitle
            type="datepicker"
            value={row?.date}
            onChange={(value) =>
              handleChange(`entries[${row?._index}].date`, value)
            }
            placeholder="Select Date"
            showDropdown={false}
          />
        ),
      },
      {
        key: "description",
        label: "Description",
        render: (row) => (
          <FormFieldSet
            noTitle
            type="text"
            value={row?.description === "-" ? "" : row?.description}
            onChange={(value) =>
              handleChange(`entries[${row?._index}].description`, value)
            }
            placeholder="Description"
          />
        ),
      },
      {
        key: "debit",
        label: "Debit",
        render: (row) => (
          <FormFieldSet
            noTitle
            type="text"
            value={row?.debit === "-" ? "" : row?.debit}
            onChange={(value) =>
              handleChange(`entries[${row?._index}].debit`, value)
            }
            placeholder="Debit"
          />
        ),
      },
      {
        key: "credit",
        label: "Credit",
        render: (row) => (
          <FormFieldSet
            noTitle
            type="text"
            value={row?.credit === "-" ? "" : row?.credit}
            onChange={(value) =>
              handleChange(`entries[${row?._index}].credit`, value)
            }
            placeholder="Credit"
          />
        ),
      },
      {
        key: "attachments",
        label: "Attachments",
        render: (row) => {
          const handleAttachmentChange = (value) => {
            // Filter out duplicates by name
            const existingNames =
              row.attachments
                ?.filter((a) => typeof a === "string")
                .map((a) => a) || [];
            const newFiles = value.filter((file) => {
              if (file instanceof File) {
                return (
                  !existingNames.includes(file.name) &&
                  !row.attachments?.some(
                    (a) => a instanceof File && a.name === file.name,
                  )
                );
              }
              return true;
            });
            const combined = [...(row.attachments || []), ...newFiles];
            handleChange(`entries[${row?._index}].attachments`, combined);
          };

          return (
            <div>
              <FormFieldSet
                noTitle
                type="file"
                onChange={handleAttachmentChange}
                placeholder="Attachments"
                multiple
              />
              {(mode === "edit" || row?.attachments?.length > 0) && (
                <div
                  className="flex rounded-md items-center cursor-pointer"
                  onClick={() => {
                    setSelectedIndex(row?._index);
                    setAttachmentModal(true);
                  }}
                >
                  <Chip
                    label={`Attachments: ${row?.attachments?.length || 0}`}
                    icon={"docs"}
                    iconPosition={"before"}
                  />
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: "employee",
        label: "Employee",
        render: (row) => (
          <FormFieldSet
            noTitle
            type="employeesSorted"
            showSearch
            value={row?.employee?.label || row?.employee || ""}
            // value={row?.employee?.label || ""}
            onChange={(value) =>
              handleChange(`entries[${row?._index}].employee`, value)
            }
            keyname="employeesSorted"
            placeholder="Select Employee"
          />
        ),
      },
      {
        key: "exchangeRate",
        label: "Exchange Rate",
        render: (row) => (
          <FormFieldSet
            noTitle
            type="text"
            value={row?.exchangeRate === "-" ? "" : row?.exchangeRate}
            onChange={(value) =>
              handleChange(`entries[${row?._index}].exchangeRate`, value)
            }
            placeholder="Rate"
          />
        ),
      },
      {
        key: "dollarAmount",
        label: "Dollar Amount",
        render: (row) => (
          <FormFieldSet
            noTitle
            type="text"
            value={row?.dollarAmount === "-" ? "" : row?.dollarAmount}
            onChange={(value) =>
              handleChange(`entries[${row?._index}].dollarAmount`, value)
            }
            placeholder="Dollar Amount"
          />
        ),
      },
      // Actions column — hidden in edit mode
      ...(mode !== "edit"
        ? [
            {
              key: "actions",
              label: "Actions",
              render: (row) => (
                <div className="flex gap-2 items-center justify-center">
                  <Button
                    type="icon"
                    icon="add"
                    size="xs"
                    color="black"
                    onClick={addRow}
                    className="p-3"
                  />
                  {formData?.entries?.length > 1 && (
                    <Button
                      type="icon"
                      icon="remove"
                      size="xs"
                      color="danger"
                      onClick={() => removeRow(row?.id)}
                      className="p-3"
                    />
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [
      mode,
      formData,
      employeeOptions,
      addRow,
      removeRow,
      setSelectedIndex,
      setAttachmentModal,
    ],
  );

  const entriesWithIndex = useMemo(
    () =>
      formData?.entries?.map((entry, index) => ({ ...entry, _index: index })),
    [formData?.entries],
  );
  const mappedRows = useMemo(
    () => mapTableRows(entriesWithIndex, columns),
    [entriesWithIndex, columns],
  );

  return (
    <div className="px-6 h-full flex flex-col w-full">
      <div className="flex justify-between items-center mb-6">
        <PageHeader
          Section={
            <PageTitle
              title={"Journal Entries"}
              tag="h5"
              size={"sm"}
              className="text-primary-500 font-semibold"
            />
          }
        />
        <Button
          size="sm"
          title="Back"
          icon="arrow_left_alt"
          onClick={() => {
            handleReset();
            onBack();
          }}
        />
      </div>

      <TableAdv
        tableId={
          mode === "create"
            ? "banking_journalentry_create"
            : "banking_journalentry_edit"
        }
        rows={mappedRows}
        columns={columns}
      />

      <div className="flex justify-end gap-3 mt-auto pt-6">
        <Button
          title="Cancel"
          variant="outlined"
          onClick={() => {
            handleReset();
            onBack();
          }}
          disabled={loading || submitting}
        />
        <Button
          title={"Save & Close"}
          onClick={handleSave}
          disabled={loading || submitting}
        />
      </div>
      <Modal
        isOpen={attachmentModal}
        onClose={() => setAttachmentModal(false)}
        width="max-w-2xl"
        title={"Manage Attachments"}
      >
        <div className="flex flex-col gap-3 max-h-[400px] overflow-auto">
          {existingAttachments.length === 0 && newAttachments.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-6">
              No attachments available
            </div>
          ) : (
            <>
              {existingAttachments.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">
                    Existing Attachments
                  </h4>
                  <DocumentsList data={existingAttachmentDocs} />
                </div>
              )}
              {newAttachments.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">
                    New Attachments
                  </h4>
                  {newAttachments.map((file, i) => (
                    <div
                      key={`new-${i}`}
                      className="flex items-center justify-between border rounded-lg px-3 py-2 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <span className="material-icons text-gray-500 text-lg">
                          description
                        </span>
                        <span className="text-sm text-gray-700 truncate max-w-[200px]">
                          {file.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="icon"
                          icon="visibility"
                          size="md"
                          color="black"
                          variant="text"
                          onClick={() => {
                            const url = URL.createObjectURL(file);
                            window.open(url);
                          }}
                        />
                        <Button
                          type="icon"
                          icon="delete"
                          size="md"
                          color="danger"
                          variant="text"
                          onClick={() => handleRemoveAttachment(i, true)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default JournalEntry;